// Preferências (Etapa 14 — S01 §Preferências, S03 §Notificações): as duas
// notificações locais, manter tela ligada, reduzir brilho.
//
// Por que AsyncStorage e não uma coluna em `profiles`: mesmo raciocínio de
// src/features/onboarding/onboardingFlag.ts — são preferências de
// DISPOSITIVO (não precisam sincronizar entre aparelhos; "manter tela
// ligada" numa usuária não faz sentido "seguir" pra outro celular), então
// uma coluna nova em `profiles` arrastaria migração local + trigger/migração
// remota no Postgres para um dado que não precisa viajar. AsyncStorage puro
// resolve sem tocar em schema/outbox/sync.
//
// Defaults preservam o comportamento já implementado nas etapas anteriores
// (pausa/keep-awake/brilho ligados incondicionalmente) — a Etapa 14 só os
// torna DESLIGÁVEIS, nunca muda o padrão de quem nunca abriu a tela de
// Preferências. `dailyReminderEnabled` é a única preferência nova de
// verdade, e a spec pede explicitamente "off por padrão".
import AsyncStorage from '@react-native-async-storage/async-storage';

const KEY = 'crochet:preferences';

export interface Preferences {
  pauseReminderEnabled: boolean;
  dailyReminderEnabled: boolean;
  /** Horário do lembrete diário, 0–23 / 0–59 (hora local do aparelho). */
  dailyReminderHour: number;
  dailyReminderMinute: number;
  keepScreenOnEnabled: boolean;
  reduceBrightnessEnabled: boolean;
}

export const DEFAULT_PREFERENCES: Preferences = {
  pauseReminderEnabled: true,
  dailyReminderEnabled: false,
  dailyReminderHour: 9,
  dailyReminderMinute: 0,
  keepScreenOnEnabled: true,
  reduceBrightnessEnabled: true,
};

/** Devolve os defaults quando nada foi salvo ainda; mescla com o default
 * (em vez de exigir o objeto completo) para não quebrar se uma preferência
 * nova for adicionada depois e usuárias antigas tiverem um blob incompleto
 * salvo. */
export async function getPreferences(): Promise<Preferences> {
  const raw = await AsyncStorage.getItem(KEY);
  if (!raw) return DEFAULT_PREFERENCES;
  try {
    return { ...DEFAULT_PREFERENCES, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

/** Aplica um patch parcial sobre as preferências atuais e persiste o
 * resultado completo. Devolve o objeto atualizado (a tela usa o retorno
 * pra atualizar o estado local sem reler o AsyncStorage). */
export async function setPreferences(patch: Partial<Preferences>): Promise<Preferences> {
  const current = await getPreferences();
  const next = { ...current, ...patch };
  await AsyncStorage.setItem(KEY, JSON.stringify(next));
  return next;
}
